// PURE chute-stroke path builder (IMPLEMENTATION §2.7 PAINT, UX §3.3 PAINT_CHUTE). The finger's cell is
// appended as a 4-connected path:
//   - a diagonal (or longer) jump is walked cell by cell; at a diagonal step the DOMINANT axis of the finger's
//     motion goes first (the inserted corner cell);
//   - re-entering the previous cell pops the last one (backtrack); re-entering an earlier cell rewinds the
//     path to it (the sim refuses a path that visits a cell twice);
//   - a blocked cell (a loose item, rock, outside the board) ends the stroke there: `blocked` is the red-X
//     cell and later moves are ignored;
//   - buildings (not chutes) may be the first cell (the source) and the last one (the sink): after a sink
//     the path only backtracks.
// The router classifies cells from the sim (query.cellView); `query.paintPreview` then says which cells the
// stroke would actually place (the unaffordable tail is shown red, never placed).
import type { Cell } from '../layout/computeLayout.ts';

export type CellClass = 'open' | 'chute' | 'building' | 'blocked';

export interface PaintPath {
  cells: Cell[];
  /** The cell that ended the stroke (red X), or null while it is still open. */
  blocked: Cell | null;
}

const eq = (a: Cell, b: Cell): boolean => a.x === b.x && a.y === b.y;

export function startPath(cell: Cell): PaintPath {
  return { cells: [{ x: cell.x, y: cell.y }], blocked: null };
}

/** True when the path ends on a sink building (it can only backtrack now). */
export function endsOnSink(p: PaintPath, classify: (c: Cell) => CellClass): boolean {
  const last = p.cells[p.cells.length - 1];
  return p.cells.length >= 2 && last !== undefined && classify(last) === 'building';
}

/** One 4-connected step from `from` toward `to` (the dominant remaining axis first; ties by `preferX`). */
function stepToward(from: Cell, to: Cell, preferX: boolean): Cell {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  const alongX = ax > ay || (ax === ay && preferX);
  if (alongX) return { x: from.x + Math.sign(dx), y: from.y };
  return { x: from.x, y: from.y + Math.sign(dy) };
}

/**
 * Extends the stroke toward the finger's cell `to`. `dominantX`: the finger moved more along x than y since
 * the last cell (decides the corner of a diagonal step).
 */
export function extendPath(
  p: PaintPath,
  to: Cell,
  dominantX: boolean,
  classify: (c: Cell) => CellClass,
): PaintPath {
  if (p.blocked !== null) return p;
  let cells = p.cells;
  const start = cells[cells.length - 1];
  // every iteration moves the path's end one step closer to `to` (append or rewind to the stepped cell)
  let guard = start === undefined ? 0 : Math.abs(to.x - start.x) + Math.abs(to.y - start.y);
  while (guard-- > 0) {
    const last = cells[cells.length - 1];
    if (last === undefined || eq(last, to)) break;
    const next = stepToward(last, to, dominantX);
    const at = cells.findIndex((c) => eq(c, next));
    if (at >= 0) {
      cells = cells.slice(0, at + 1); // backtrack (previous cell) / rewind (an earlier one)
      continue;
    }
    if (endsOnSink({ cells, blocked: null }, classify)) break; // after a sink: backtrack only
    const cls = classify(next);
    if (cls === 'blocked') return { cells, blocked: next };
    cells = [...cells, next];
  }
  return cells === p.cells ? p : { cells, blocked: null };
}

/** Direction of the stroke's last segment (N/E/S/W), or null for a one-cell stroke. */
export function lastDir(p: PaintPath): 'N' | 'E' | 'S' | 'W' | null {
  const n = p.cells.length;
  const a = p.cells[n - 2];
  const b = p.cells[n - 1];
  if (a === undefined || b === undefined) return null;
  if (b.x > a.x) return 'E';
  if (b.x < a.x) return 'W';
  return b.y > a.y ? 'S' : 'N';
}
